import {
  AdminApiError,
  type AdminClient,
  type AdminUserView,
  type IntegratorSummary,
} from '@apx/admin-sdk';
import { emptyState } from '../empty-state.js';
import { askConfirm, customModal, html } from '../modal.js';
import { fmtDate, fmtDateTime } from '../format.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';
import { icon } from '../icons.js';

const ROLE_OPTIONS = ['super_admin', 'integrator_admin', 'ops', 'finance', 'support', 'developer'];

export interface AdminUsersPageDeps {
  client: AdminClient;
  integrators: IntegratorSummary[];
}

export async function renderAdminUsersPage(
  root: HTMLElement,
  deps: AdminUsersPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Admin users</h1>
    <p class="page-sub">Create operators, reset passwords, and grant/revoke role bindings (global or integrator-scoped). Only <code>super_admin</code> can manage admin users.</p>

    <div id="au-stats" class="players-stats"></div>

    <div class="card">
      <div class="detail-card-head" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px">
        <div>
          <h2>Users</h2>
          <span class="muted">A user cannot sign in until granted either a role or, for integrator staff, capabilities on an integrator.</span>
        </div>
        <button id="au-create" class="primary" style="white-space:nowrap">+ New admin user</button>
      </div>
      <div id="au-list">${skeletonTable({ rows: 5, columns: 7 })}</div>
    </div>
  `;

  const listEl = root.querySelector<HTMLElement>('#au-list')!;
  const statsEl = root.querySelector<HTMLElement>('#au-stats')!;

  async function refresh(): Promise<void> {
    try {
      const users = await deps.client.listAdminUsers();
      renderStats(users);
      listEl.innerHTML = renderTable(users);
      bindRowActions();
    } catch (err) {
      statsEl.innerHTML = '';
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderStats(users: AdminUserView[]): void {
    const active = users.filter((u) => u.status === 'active').length;
    // "With roles" counted role bindings only, so every capability-only
    // integrator staff member read as having no access at all.
    const withAccess = users.filter((u) => hasAccess(u)).length;
    const tenantStaff = users.filter((u) => u.roles.length === 0 && u.capabilityGrants.length > 0).length;
    const card = (label: string, value: string): string =>
      `<div class="stat-card"><div class="stat-card-label">${label}</div><div class="stat-card-value">${value}</div></div>`;
    statsEl.innerHTML = `
      ${card('Total users', String(users.length))}
      ${card('Active', String(active))}
      ${card('With access', String(withAccess))}
      ${card('Integrator staff', String(tenantStaff))}
    `;
  }

  function hasAccess(u: AdminUserView): boolean {
    return u.roles.length > 0 || u.capabilityGrants.length > 0;
  }

  /** Whose data this user can reach — the question the roles column never answered. */
  function scopeCell(u: AdminUserView): string {
    if (u.roles.some((r) => !r.integratorId)) {
      return '<span class="pill">Provider · all integrators</span>';
    }
    const names = new Map<string, string>();
    for (const r of u.roles) {
      if (r.integratorId) names.set(r.integratorId, r.tenantSlug ?? r.integratorId.slice(0, 8));
    }
    for (const c of u.capabilityGrants) names.set(c.integratorId, c.tenantName);
    if (names.size === 0) return '<span class="muted">—</span>';
    return [...names.entries()]
      .map(([id, name]) => `<a class="pill" href="/integrators/${encodeURIComponent(id)}/staff">${escapeHtml(name)}</a>`)
      .join(' ');
  }

  function renderTable(users: AdminUserView[]): string {
    if (users.length === 0) {
      return emptyState({
        icon: 'list',
        title: 'No admin users yet',
        hint: 'Create one with “+ New admin user”. They stay inactive until you grant a role.',
      });
    }
    return `<table>
      <thead><tr><th>User</th><th>Status</th><th>Scope</th><th>Access</th><th>Last login</th><th>Created</th><th style="text-align:right">Actions</th></tr></thead>
      <tbody>${users.map(rowHtml).join('')}</tbody>
    </table>`;
  }

  function rowHtml(u: AdminUserView): string {
    const rolePills = u.roles
      .map(
        (r) => `<span class="au-role-pill">
          <code>${escapeHtml(r.role)}</code>
          <span class="muted au-role-scope">${r.tenantSlug ? `@${escapeHtml(r.tenantSlug)}` : 'global'}</span>
          <button data-revoke="${u.id}:${r.grantId}" class="au-role-x" aria-label="Revoke ${escapeHtml(r.role)}">×</button>
        </span>`,
      )
      .join('');
    // Capabilities are ticked per-user on the integrator's own Staff page, so
    // these are a labelled link there rather than revocable pills here.
    const capPills = u.capabilityGrants
      .map(
        (c) => `<a class="au-role-pill" href="/integrators/${encodeURIComponent(c.integratorId)}/staff" title="Edit on ${escapeHtml(c.tenantName)}’s staff page">
          <code>staff</code>
          <span class="muted au-role-scope">${c.count} ${c.count === 1 ? 'capability' : 'capabilities'}</span>
        </a>`,
      )
      .join('');
    const access = rolePills + capPills;
    return `<tr>
      <td>
        <div class="au-user">
          <span class="au-avatar">${escapeHtml(monogram(u.email))}</span>
          <div style="min-width:0">
            <div class="au-user-email">${escapeHtml(u.email)}</div>
            <div class="muted"><code style="font-size:11px">${u.id.slice(0, 8)}…</code></div>
          </div>
        </div>
      </td>
      <td><span class="pill ${u.status === 'active' ? 'live' : 'warn'}">${u.status}</span></td>
      <td>${scopeCell(u)}</td>
      <td><div class="au-roles">${access || '<span class="muted">no access</span>'}</div></td>
      <td>${u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : '<span class="muted">never</span>'}</td>
      <td><span class="muted">${fmtDate(u.createdAt)}</span></td>
      <td class="au-actions">
        <button class="icon-act" data-grant="${u.id}" data-tip="Grant role" aria-label="Grant role">${icon('shield', { size: 15 })}</button>
        <button class="icon-act" data-password="${u.id}" data-tip="Reset password" aria-label="Reset password">${icon('key', { size: 15 })}</button>
        <button class="icon-act ${u.status === 'active' ? '' : 'icon-act-active'}" data-toggle="${u.id}:${u.status}" data-tip="${u.status === 'active' ? 'Disable user' : 'Activate user'}" aria-label="Toggle status">${icon(u.status === 'active' ? 'ban' : 'check', { size: 15 })}</button>
      </td>
    </tr>`;
  }

  function bindRowActions(): void {
    listEl.querySelectorAll<HTMLButtonElement>('button[data-revoke]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const [userId, grantId] = btn.dataset.revoke!.split(':');
        if (!(await askConfirm('Revoke this role binding?'))) return;
        try {
          await deps.client.revokeAdminRole(userId!, grantId!);
          showToast('Role revoked.', 'success');
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });

    listEl.querySelectorAll<HTMLButtonElement>('button[data-grant]').forEach((btn) => {
      btn.addEventListener('click', () => void openGrantModal(btn.dataset.grant!));
    });

    listEl.querySelectorAll<HTMLButtonElement>('button[data-password]').forEach((btn) => {
      btn.addEventListener('click', () => void openResetPwdModal(btn.dataset.password!));
    });

    listEl.querySelectorAll<HTMLButtonElement>('button[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const [userId, current] = btn.dataset.toggle!.split(':');
        const next = current === 'active' ? 'disabled' : 'active';
        if (next === 'disabled' && !(await askConfirm('Disable this user? They lose access immediately.'))) return;
        try {
          await deps.client.updateAdminUser(userId!, { status: next });
          showToast(next === 'active' ? 'Activated.' : 'Disabled.', 'success');
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
  }

  async function openCreateModal(): Promise<void> {
    // Creating an account and granting it access were two separate steps, so
    // the normal outcome of this dialog was a user who could not sign in. The
    // role is asked for here and granted immediately after creation.
    const roleOpts = [
      html`<option value="">No role yet — grant later</option>`,
      ...ROLE_OPTIONS.map((r) => html`<option value="${r}">${r}</option>`),
    ];
    const scopeOpts = [
      html`<option value="">Global (super_admin only)</option>`,
      ...deps.integrators.map((t) => html`<option value="${t.id}">${t.name} · ${t.slug}</option>`),
    ];
    const result = await customModal<{ email: string; password: string; role?: string; integratorId?: string }>({
      title: 'New admin user',
      confirmLabel: 'Create user',
      body: html`
        <div class="modal-field">
          <label class="modal-field-label">Email</label>
          <input id="au-m-email" type="email" placeholder="ops@example.com" class="modal-field-input" />
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Password (min 8 chars)</label>
          <input id="au-m-password" type="password" class="modal-field-input" />
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Role</label>
          <select id="au-m-role" class="modal-field-input">${roleOpts}</select>
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Scope</label>
          <select id="au-m-scope" class="modal-field-input">${scopeOpts}</select>
        </div>
        <p class="muted" style="font-size:12px;margin:0">
          Without a role the user is created inactive and cannot sign in. To add
          an integrator’s own staff — finance, support — use that integrator’s
          Staff page instead: they get capabilities, not a role.
        </p>
        <p id="au-m-err" class="error" style="margin:8px 0 0;font-size:12px;min-height:14px"></p>
      `,
      parseOnConfirm: (r) => {
        const email = r.querySelector<HTMLInputElement>('#au-m-email')!.value.trim();
        const password = r.querySelector<HTMLInputElement>('#au-m-password')!.value;
        const role = r.querySelector<HTMLSelectElement>('#au-m-role')!.value || undefined;
        const integratorId = r.querySelector<HTMLSelectElement>('#au-m-scope')!.value || undefined;
        const err = r.querySelector<HTMLElement>('#au-m-err')!;
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          err.textContent = 'Enter a valid email.';
          return null;
        }
        if (password.length < 8) {
          err.textContent = 'Password must be at least 8 characters.';
          return null;
        }
        return { email, password, role, integratorId };
      },
    });
    if (!result) return;
    try {
      const created = await deps.client.createAdminUser({ email: result.email, password: result.password });
      if (!result.role) {
        showToast(`Created ${created.email} — inactive until granted a role.`, 'success');
      } else {
        // Separate call: the account exists either way, so a rejected grant
        // (global needs super_admin) must not read as a failed creation.
        try {
          await deps.client.grantAdminRole(created.id, {
            role: result.role,
            ...(result.integratorId ? { integratorId: result.integratorId } : {}),
          });
          showToast(`Created ${created.email} with ${result.role}.`, 'success');
        } catch (grantErr) {
          showToast(`Created ${created.email}, but the role was not granted: ${describe(grantErr)}`, 'error');
        }
      }
      await refresh();
    } catch (err) {
      showToast(describe(err), 'error');
    }
  }

  async function openGrantModal(userId: string): Promise<void> {
    // `html` auto-escapes interpolations, so options must be html fragments
    // (an array of fragments renders each, not the escaped markup string).
    const roleOpts = ROLE_OPTIONS.map((r) => html`<option value="${r}">${r}</option>`);
    const scopeOpts = [
      html`<option value="">Global (super_admin only)</option>`,
      ...deps.integrators.map((t) => html`<option value="${t.id}">${t.name} · ${t.slug}</option>`),
    ];
    const result = await customModal<{ role: string; integratorId?: string }>({
      title: 'Grant role',
      confirmLabel: 'Grant',
      body: html`
        <div class="modal-field">
          <label class="modal-field-label">Role</label>
          <select id="au-g-role" class="modal-field-input">${roleOpts}</select>
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Scope</label>
          <select id="au-g-scope" class="modal-field-input">${scopeOpts}</select>
        </div>
        <p class="muted" style="font-size:12px;margin:0">Integrator-scoped roles only apply to that integrator. Global needs super_admin.</p>
      `,
      parseOnConfirm: (r) => {
        const role = r.querySelector<HTMLSelectElement>('#au-g-role')!.value;
        const integratorId = r.querySelector<HTMLSelectElement>('#au-g-scope')!.value || undefined;
        return { role, integratorId };
      },
    });
    if (!result) return;
    try {
      await deps.client.grantAdminRole(userId, result);
      showToast('Role granted.', 'success');
      await refresh();
    } catch (err) {
      showToast(describe(err), 'error');
    }
  }

  async function openResetPwdModal(userId: string): Promise<void> {
    const result = await customModal<{ password: string }>({
      title: 'Reset password',
      confirmLabel: 'Reset',
      danger: true,
      body: html`
        <div class="modal-field">
          <label class="modal-field-label">New password (min 8 chars)</label>
          <input id="au-p-pwd" type="text" placeholder="new password" class="modal-field-input" />
        </div>
        <p class="muted" style="font-size:12px;margin:0">Shown as plain text so you can copy it to hand off. The user can change it after sign-in.</p>
        <p id="au-p-err" class="error" style="margin:8px 0 0;font-size:12px;min-height:14px"></p>
      `,
      parseOnConfirm: (r) => {
        const password = r.querySelector<HTMLInputElement>('#au-p-pwd')!.value;
        const err = r.querySelector<HTMLElement>('#au-p-err')!;
        if (password.length < 8) {
          err.textContent = 'Password must be at least 8 characters.';
          return null;
        }
        return { password };
      },
    });
    if (!result) return;
    try {
      await deps.client.updateAdminUser(userId, { password: result.password });
      showToast('Password reset.', 'success');
    } catch (err) {
      showToast(describe(err), 'error');
    }
  }

  root.querySelector<HTMLButtonElement>('#au-create')!.addEventListener('click', () => void openCreateModal());

  await refresh();
}

/** Two-letter monogram from an email's local part (e.g. ops@… → "OP"). */
function monogram(email: string): string {
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  const chars = parts.length >= 2 ? parts[0]![0]! + parts[1]![0]! : local.slice(0, 2);
  return chars.toUpperCase() || '?';
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
